import {
  getDefaultStages,
  getMatterTypeForCase,
  type CaseTaxonomyContextInput,
} from './caseTaxonomyRegistry';

/** Same matching used by Case stages panel, drafts grouping, and list filters. */
export function stageLabelsMatch(a: string, b: string): boolean {
  const x = a.toLowerCase().trim();
  const y = b.toLowerCase().trim();
  return x === y || x.includes(y) || y.includes(x);
}

/** Global legacy label → preferred canonical label when matter-specific match fails. */
const GLOBAL_STAGE_ALIASES: Record<string, string> = {
  'pre filing': 'Case Preparation',
  'drafting / pre filing': 'Case Preparation',
  disposed: 'Closed',
  'institution / filing of plaint': 'Filing / Institution',
  'notice / summons': 'Summons / Notices',
  'appearance & written statement': 'Defendant Appearance / Written Statement',
  'appearance of defendant & filing of written statement': 'Defendant Appearance / Written Statement',
  'filing of written statement': 'Written Statement',
  execution: 'Execution / Appeal',
  'filing of bail application': 'Filing',
  'filing of bail application (sec 497/498 crpc)': 'Filing',
  'notice to state & summoning of record': 'Notice to State',
  'filing of writ petition': 'Filing',
  'case preparation / record review': 'Case Preparation',
  'petition drafting': 'Case Preparation',
  'comments / parawise comments': 'Comments / Reply',
  'comments / parawise reply': 'Comments / Reply',
  'compliance / contempt / appeal, if any': 'Compliance / Further Remedy',
  'rejoinder, if any': 'Rejoinder (if any)',
  'interim relief / stay, if any': 'Interim Relief / Stay (if any)',
  'record called, if required': 'Record Called (if required)',
  'filing of parawise comments': 'Parawise Comments',
  'fir / private complaint': 'FIR / Complaint / Case Initiation',
  'submission of challan / police report': 'Challan / Police Report',
  'order / judgment': 'Order',
  'judgment / acquittal / conviction': 'Judgment',
  'leave to defend / pla': 'Leave to Defend',
};

/**
 * Map a stored or legacy stage label to the canonical label for this matter type.
 * Returns the input unchanged when no canonical match exists.
 */
export function normalizeStageLabel(
  stage: string | undefined,
  caseData: CaseTaxonomyContextInput,
): string {
  const trimmed = stage?.trim();
  if (!trimmed) return '';

  const canonicalStages = getDefaultStages(caseData);
  const direct = canonicalStages.find((s) => stageLabelsMatch(s, trimmed));
  if (direct) return direct;

  const matterType = getMatterTypeForCase(caseData);
  const prep = matterType?.defaultStages?.[0];
  const lower = trimmed.toLowerCase();
  if (
    prep &&
    (/^pre[- ]?filing$/i.test(trimmed) ||
      /^drafting\s*\/?\s*pre[- ]?filing$/i.test(trimmed) ||
      lower === 'pre filing')
  ) {
    return prep;
  }

  const global = GLOBAL_STAGE_ALIASES[lower];
  if (global) {
    const hit = canonicalStages.find((s) => stageLabelsMatch(s, global));
    if (hit) return hit;
    return global;
  }

  const fuzzy = canonicalStages.find(
    (s) =>
      s.toLowerCase().includes(lower) ||
      lower.includes(s.toLowerCase()),
  );
  if (fuzzy) return fuzzy;

  return trimmed;
}
